/**
 * Turns a contract `Chart` into plot-ready rows: the x order, and each visible series' values with its fixed colour.
 * Owner: slice web-charts (#1115).
 */
import type { Chart, Series } from '../../../lib/api/generated'
import { seriesColor } from './chart-theme'
import { isVisible, pointNumber, slotStripped, type Visibility } from './format'

export type CanSee = (v: Visibility) => boolean
export type PointClick = (p: { x: string; series: string }) => void

/** Props every chart form takes. `canSee` hides series the viewer's role may not see. */
export interface ChartProps {
  chart: Chart
  onPointClick?: PointClick
  canSee?: CanSee
}

export interface PlotSeries {
  series: Series
  key: string
  label: string
  /** Index in the chart's own series list: the colour follows the entity, never its rank after filtering. */
  index: number
  color: string
  /** One per x in `xs`; null is a gap (no bar, no zero). */
  values: (number | null)[]
  ns: (number | null)[]
}

export interface PlotModel {
  xs: string[]
  /** Visible series whose state is ok: these are drawn. */
  plotted: PlotSeries[]
  /** Visible series whose state is not ok: listed with their state, never drawn as zero. */
  missing: Series[]
  min: number
  max: number
  hasData: boolean
}

export function seriesVisible(s: Series, canSee: CanSee | undefined): boolean {
  const pts = s.points ?? []
  const stripped =
    (s.visibility === 'money' || s.visibility === 'cost') && pts.length > 0 && pts.every((p) => slotStripped(p, s.visibility))
  return isVisible(s.visibility, canSee, stripped)
}

export function xOrder(chart: Chart): string[] {
  if (chart.categories && chart.categories.length > 0) return [...chart.categories]
  const seen = new Set<string>()
  const xs: string[] = []
  for (const s of chart.series ?? []) {
    for (const p of s.points ?? []) {
      if (!seen.has(p.x)) {
        seen.add(p.x)
        xs.push(p.x)
      }
    }
  }
  if (chart.x_kind !== 'category') xs.sort()
  return xs
}

export function buildModel(chart: Chart, canSee?: CanSee): PlotModel {
  const xs = xOrder(chart)
  const plotted: PlotSeries[] = []
  const missing: Series[] = []
  let min = Number.POSITIVE_INFINITY
  let max = Number.NEGATIVE_INFINITY
  ;(chart.series ?? []).forEach((s, index) => {
    if (!seriesVisible(s, canSee)) return
    if ((s.state ?? 'ok') !== 'ok') {
      missing.push(s)
      return
    }
    const byX = new Map((s.points ?? []).map((p) => [p.x, p]))
    const values: (number | null)[] = []
    const ns: (number | null)[] = []
    for (const x of xs) {
      const p = byX.get(x)
      const v = p ? pointNumber(p, s) : null
      values.push(v)
      ns.push(p?.n ?? null)
      if (v !== null) {
        min = Math.min(min, v)
        max = Math.max(max, v)
      }
    }
    plotted.push({ series: s, key: s.key, label: s.label, index, color: seriesColor(index), values, ns })
  })
  const hasData = Number.isFinite(min)
  return { xs, plotted, missing, min: hasData ? min : 0, max: hasData ? max : 0, hasData }
}

/** True when the viewer's role sees none of a chart's series, so the card is not rendered at all. */
export function chartHidden(chart: Chart, canSee?: CanSee): boolean {
  const model = buildModel(chart, canSee)
  return (chart.series ?? []).length > 0 && model.plotted.length === 0 && model.missing.length === 0
}
